import type {
  FieldDefinition,
  TableScalarValue,
  TableSourceDescriptor,
  VectorSourceDescriptor,
} from '../data/model';

const MAX_OVERLAP_CANDIDATES = 32;
const MAX_VALUES_PER_FIELD = 20_000;

type JoinKeyKind = 'number' | 'string';

interface KeyPairCandidate {
  readonly left: FieldDefinition;
  readonly leftIndex: number;
  readonly nameScore: number;
  readonly right: FieldDefinition;
  readonly rightIndex: number;
}

function keyKind(field: FieldDefinition): JoinKeyKind | null {
  if (field.type === 'integer' || field.type === 'number') return 'number';
  return field.type === 'string' ? 'string' : null;
}

export function joinKeyFields(fields: readonly FieldDefinition[]): readonly FieldDefinition[] {
  return fields.filter((field) => keyKind(field) !== null);
}

function compatible(left: FieldDefinition, right: FieldDefinition): boolean {
  return keyKind(left) === keyKind(right);
}

function fieldNameTokens(name: string): readonly string[] {
  return (name.toLowerCase().match(/[a-z]+|\d+/g) ?? []).sort();
}

function fieldNameScore(left: string, right: string): number {
  const lowerLeft = left.toLowerCase();
  const lowerRight = right.toLowerCase();
  if (lowerLeft === lowerRight) return 100;
  const compactLeft = lowerLeft.replace(/[^a-z0-9]/g, '');
  const compactRight = lowerRight.replace(/[^a-z0-9]/g, '');
  if (compactLeft === compactRight) return 96;
  const leftTokens = fieldNameTokens(left);
  const rightTokens = fieldNameTokens(right);
  if (leftTokens.join('|') === rightTokens.join('|')) return 92;
  const rightTokenSet = new Set(rightTokens);
  const shared = leftTokens.filter((token) => rightTokenSet.has(token)).length;
  const union = new Set([...leftTokens, ...rightTokens]).size;
  return union === 0 ? 0 : Math.round((shared / union) * 70);
}

function compareNameCandidates(a: KeyPairCandidate, b: KeyPairCandidate): number {
  return b.nameScore - a.nameScore || a.leftIndex - b.leftIndex || a.rightIndex - b.rightIndex;
}

function candidatePairs(
  leftFields: readonly FieldDefinition[],
  rightFields: readonly FieldDefinition[],
): readonly KeyPairCandidate[] {
  const candidates: KeyPairCandidate[] = [];
  leftFields.forEach((left, leftIndex) => {
    rightFields.forEach((right, rightIndex) => {
      if (!compatible(left, right)) return;
      candidates.push({
        left,
        leftIndex,
        nameScore: fieldNameScore(left.name, right.name),
        right,
        rightIndex,
      });
    });
  });
  return candidates.sort(compareNameCandidates).slice(0, MAX_OVERLAP_CANDIDATES);
}

function lookupKey(value: unknown, kind: JoinKeyKind): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (kind === 'number' && typeof value === 'number' && Number.isFinite(value)) {
    return `n:${Object.is(value, -0) ? 0 : value}`;
  }
  return kind === 'string' && typeof value === 'string' ? `s:${value}` : null;
}

function vectorValues(source: VectorSourceDescriptor, field: FieldDefinition): ReadonlySet<string> {
  const kind = keyKind(field);
  const values = new Set<string>();
  if (!kind) return values;
  const limit = Math.min(source.data.features.length, MAX_VALUES_PER_FIELD);
  for (let index = 0; index < limit; index += 1) {
    const key = lookupKey(source.data.features[index]?.properties?.[field.name], kind);
    if (key !== null) values.add(key);
  }
  return values;
}

function tableValues(source: TableSourceDescriptor, field: FieldDefinition): ReadonlySet<string> {
  const kind = keyKind(field);
  const column = source.fields.findIndex((candidate) => candidate.name === field.name);
  const values = new Set<string>();
  if (!kind || column < 0) return values;
  const limit = Math.min(source.rows.length, MAX_VALUES_PER_FIELD);
  for (let index = 0; index < limit; index += 1) {
    const value: TableScalarValue | undefined = source.rows[index]?.[column];
    const key = lookupKey(value, kind);
    if (key !== null) values.add(key);
  }
  return values;
}

function overlapRatio(left: ReadonlySet<string>, right: ReadonlySet<string>): number {
  const denominator = Math.min(left.size, right.size);
  if (denominator === 0) return 0;
  const smaller = left.size <= right.size ? left : right;
  const larger = smaller === left ? right : left;
  let matches = 0;
  for (const value of smaller) if (larger.has(value)) matches += 1;
  return matches / denominator;
}

export interface SuggestedJoinKeys {
  readonly left: string;
  readonly right: string;
}

/**
 * Suggests one compatible native key pair. Field names are compared permissively, but sampled key
 * values use the exact same typed, case-sensitive representation as the join itself.
 */
export function suggestJoinKeys(
  source: VectorSourceDescriptor,
  table: TableSourceDescriptor,
): SuggestedJoinKeys {
  const leftFields = joinKeyFields(source.fields);
  const rightFields = joinKeyFields(table.fields);
  const candidates = candidatePairs(leftFields, rightFields);
  if (candidates.length === 0) {
    return { left: leftFields[0]?.name ?? '', right: rightFields[0]?.name ?? '' };
  }

  const leftValueCache = new Map<string, ReadonlySet<string>>();
  const rightValueCache = new Map<string, ReadonlySet<string>>();
  const scored = candidates.map((candidate) => {
    const leftValues =
      leftValueCache.get(candidate.left.name) ?? vectorValues(source, candidate.left);
    const rightValues =
      rightValueCache.get(candidate.right.name) ?? tableValues(table, candidate.right);
    leftValueCache.set(candidate.left.name, leftValues);
    rightValueCache.set(candidate.right.name, rightValues);
    return {
      candidate,
      score: candidate.nameScore + overlapRatio(leftValues, rightValues) * 200,
    };
  });
  scored.sort((a, b) => b.score - a.score || compareNameCandidates(a.candidate, b.candidate));
  const suggestion = scored[0]?.candidate ?? candidates[0]!;
  return { left: suggestion.left.name, right: suggestion.right.name };
}
